// ===== Types for GL Upload module =====

export interface UploadRecord {
  id: number;
  entity_code: string;
  period: string;
  filename: string;
  status: string;
  row_count: number | null;
  rows_ingested: number | null;
  rows_rejected: number | null;
  exceptions_detected: number | null;
  uploaded_at: string | null;
  uploaded_by: string;
  is_superseded: boolean;
  processing_time_ms: number | null;
}

export interface UploadStatus {
  id: number;
  entity_code: string;
  period: string;
  filename: string;
  status: string;
  status_detail: string | null;
  row_count: number | null;
  rows_ingested: number | null;
  rows_rejected: number | null;
  exceptions_detected: number | null;
  jes_created: number | null;
  actions_created: number | null;
  uploaded_at: string | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
  uploaded_by: string;
  is_superseded: boolean;
  processing_time_ms: number | null;
  rejected_rows: { row_number: number; raw_data: any; reason: string; code: string }[];
}

export interface MappingConfig {
  id: number;
  name: string;
  description: string | null;
  column_map: Record<string, string>;
  date_format: string;
  amount_format: string;
  is_default: boolean;
  created_at: string | null;
  created_by: string;
}

export interface CoaAccount {
  account_code: string;
  account_name: string;
  account_type: string;
  statement: string;
  normal_balance: string;
  cf_classification: string | null;
  is_intercompany: boolean;
  entity_code: string | null;
  source: string;
  is_active: boolean;
  display_order: number | null;
}

// ===== Pipeline Steps =====

export const PIPELINE_STEPS = [
  { key: 'UPLOADED', label: 'Uploaded' },
  { key: 'VALIDATING', label: 'Validating' },
  { key: 'MAPPING', label: 'Mapping columns' },
  { key: 'INGESTING', label: 'Ingesting into shadow ledger' },
  { key: 'DETECTING', label: 'Detecting exceptions' },
  { key: 'DIAGNOSING', label: 'AI Diagnosis' },
  { key: 'ASSIGNING', label: 'Assignment' },
  { key: 'AUTO_APPROVING', label: 'Auto-Approval' },
];

export const TERMINAL_STATUSES = ['COMPLETE', 'FAILED', 'SUPERSEDED'];

export const DELETABLE_STATUSES = new Set(['FAILED', 'CANCELLED', 'UPLOADED', 'COMPLETE', 'VALIDATING', 'INGESTING', 'DETECTING', 'DIAGNOSING', 'ASSIGNING']);

export function getStepIndex(status: string): number {
  const idx = PIPELINE_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : -1;
}

// ===== Shared styles =====

export const TYPE_COLORS: Record<string, string> = {
  ASSET: 'var(--navy-600)',
  LIABILITY: '#f97316',
  EQUITY: 'var(--navy-800)',
  REVENUE: 'var(--positive)',
  EXPENSE: '#ef4444',
};

export const selectStyle: React.CSSProperties = {
  padding: '6px 10px', background: 'var(--bg-card)', border: '1px solid var(--border-default)',
  borderRadius: 6, color: 'var(--text-primary)', fontSize: 11, display: 'block',
};

export const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)',
  textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4,
};

export const btnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
};

export const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700,
  color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

// ===== Shared sub-components =====

export function StatRow({ label, value }: { label: string; value: number | null }) {
  return (
    <>
      <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)' }}>
        {value != null ? value.toLocaleString() : '-'}
      </span>
    </>
  );
}

export function SmallBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: 4, fontSize: 10, fontWeight: 600,
        background: 'rgba(0,0,0,0.04)', border: '1px solid var(--border-subtle)',
        color: 'var(--text-secondary)', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { color: string; bg: string }> = {
    COMPLETE: { color: 'var(--positive)', bg: 'rgba(34,197,94,0.12)' },
    FAILED: { color: '#ef4444', bg: '#fef2f2' },
    UPLOADED: { color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' },
    VALIDATING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    MAPPING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    INGESTING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    DETECTING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    DIAGNOSING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    ASSIGNING: { color: 'var(--navy-700)', bg: 'rgba(59,130,246,0.12)' },
    SUPERSEDED: { color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
  };
  const c = colors[status] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      color: c.color, background: c.bg,
    }}>
      {status}
    </span>
  );
}
